import { type FC, useEffect, useMemo, useState } from "react";

import { CourseAdminSelectionList } from "../content/courses/CourseAdminSelectionList";

import { serverManageCourseAdmin } from "api/api-server-actions";
import type { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { slices, useAppDispatch } from "store/index";
import { courseGroupingUserSelectors, courseUserSelectors } from "store/selectors";
import { selectCourseGroupingsWithCourses, selectUngroupedCourses } from "store/selectors/section";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { filterMap } from "utils/collections";

type ManageCourseAdminModalProps = {
  userId: string;
  userName: string;
  onClose: () => void;
};

const coursesIncludedInGroupings = (
  groupingIds: string[],
  courses: Pick<CourseType, "id" | "grouping_id">[],
): string[] => {
  const groupingIdSet = new Set(groupingIds);
  return filterMap(
    courses,
    (course) => Boolean(course.grouping_id && groupingIdSet.has(course.grouping_id)),
    (course) => course.id,
  );
};

export const ManageCourseAdminModal: FC<ManageCourseAdminModalProps> = ({
  userId,
  userName,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const groupingsWithCourses = useListSelector(selectCourseGroupingsWithCourses, [
    "id",
    "title",
    "courses",
  ]);
  const ungroupedCourses = useListSelector(selectUngroupedCourses, [
    "id",
    "grouping_id",
    "code",
    "title",
  ]);

  const courses = useMemo(
    () => [...groupingsWithCourses.flatMap((grouping) => grouping.courses), ...ungroupedCourses],
    [groupingsWithCourses, ungroupedCourses],
  );

  const existingCourseUsers = useListSelector(
    (state) => courseUserSelectors.filterByUserId(state, userId),
    ["id", "course_id"],
  );
  const existingCourseGroupingUsers = useListSelector(
    (state) => courseGroupingUserSelectors.filterByUserId(state, userId),
    ["id", "course_grouping_id"],
  );

  const [selectedGroupingIds, setSelectedGroupingIds] = useState<string[]>([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const groupingIds = existingCourseGroupingUsers.map((gu) => gu.course_grouping_id);
    const courseIds = existingCourseUsers.map((cu) => cu.course_id);
    const groupedCourseIds = coursesIncludedInGroupings(groupingIds, courses);

    const groupedCourseIdSet = new Set(groupedCourseIds);
    setSelectedGroupingIds([...groupingIds]);
    setSelectedCourseIds(courseIds.filter((id) => !groupedCourseIdSet.has(id)));

    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [userId, existingCourseUsers, existingCourseGroupingUsers, courses]);

  const effectiveCourseCount = useMemo(() => {
    const ids = new Set(selectedCourseIds);

    const groupedCourseIds = coursesIncludedInGroupings(selectedGroupingIds, courses);
    groupedCourseIds.forEach((id) => ids.add(id));

    return ids.size;
  }, [selectedCourseIds, selectedGroupingIds, courses]);

  const wasCourseAdmin = existingCourseUsers.length > 0 || existingCourseGroupingUsers.length > 0;
  const isRemovingCourseAdminAccess = wasCourseAdmin && effectiveCourseCount === 0;

  const toggleGroupingChecked = (groupingId: string) => {
    const willSelect = !selectedGroupingIds.includes(groupingId);
    if (willSelect) {
      const idsInGroup = filterMap(
        courses,
        (course) => course.grouping_id === groupingId,
        (course) => course.id,
      );
      const idsInGroupSet = new Set(idsInGroup);
      setSelectedCourseIds((prevC) => prevC.filter((id) => !idsInGroupSet.has(id)));
    }
    setSelectedGroupingIds((prevG) =>
      willSelect ? [...prevG, groupingId] : prevG.filter((id) => id !== groupingId),
    );
  };

  const toggleCourseChecked = (courseId: string) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId],
    );
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const { course_users: updatedCourseUsers, course_grouping_users: updatedGroupingUsers } =
        await serverManageCourseAdmin(userId, selectedCourseIds, selectedGroupingIds);

      const removeCourseUsers = existingCourseUsers.filter(
        (cu) => !updatedCourseUsers.some((newCu) => newCu.id === cu.id),
      );
      removeCourseUsers.forEach((cu) =>
        dispatch(slices.course_users.actions.removeOne({ id: cu.id })),
      );
      dispatch(slices.course_users.actions.updateMany(updatedCourseUsers));

      const removeGroupingUsers = existingCourseGroupingUsers.filter(
        (gu) => !updatedGroupingUsers.some((ng) => ng.id === gu.id),
      );
      removeGroupingUsers.forEach((gu) =>
        dispatch(slices.course_grouping_users.actions.removeOne({ id: gu.id })),
      );

      dispatch(slices.course_grouping_users.actions.updateMany(updatedGroupingUsers));

      toastSuccessMessage(t("user_options_menu.manage_course_admin.success"));
      onClose();
    } catch (error) {
      toastLocalizedOperationCatcher("manage_course_admin")(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasAnyRows = groupingsWithCourses.length > 0 || ungroupedCourses.length > 0;

  return (
    <div>
      <div className="flex flex-col gap-4">
        <div className="text-sm font-medium text-black-tint-55">{userName}</div>

        <div className="text-sm font-normal">
          {t("user_options_menu.manage_course_admin.description")}
        </div>

        <div className="flex flex-col gap-2">
          <div className="font-medium">
            {t("user_options_menu.manage_course_admin.select_courses")}
          </div>

          {hasAnyRows ? (
            <CourseAdminSelectionList
              groupingsWithCourses={groupingsWithCourses}
              selectedCourseIds={selectedCourseIds}
              selectedGroupingIds={selectedGroupingIds}
              ungroupedCourses={ungroupedCourses}
              onCourseToggle={toggleCourseChecked}
              onGroupingToggle={toggleGroupingChecked}
            />
          ) : (
            <div className="text-black-tint-30">
              {t("user_options_menu.manage_course_admin.no_courses")}
            </div>
          )}

          {isRemovingCourseAdminAccess ? (
            <div className="text-xs text-red-shade-20">
              {t("user_options_menu.manage_course_admin.no_courses_warning")}
            </div>
          ) : (
            <div className="text-xs text-black-tint-55">
              {t("user_options_menu.manage_course_admin.course_admin_of_count", {
                count: effectiveCourseCount,
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 flex w-full justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={(effectiveCourseCount === 0 && !isRemovingCourseAdminAccess) || isSubmitting}
          kind="accent"
          size="m"
          title={t("user_options_menu.manage_course_admin.submit")}
          onClick={handleSubmit}
        >
          {t("user_options_menu.manage_course_admin.submit")}
        </SquareButton>
      </div>
    </div>
  );
};
